import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import ProviderAvatar from 'components/integrations/ProviderAvatar';

const buildCurrentLabel = (userData, t) => {
  const companyName = userData?.company?.name || '';
  const integrationName =
    userData?.activeIntegration?.display_name ||
    userData?.providers?.MERCADO_LIBRE?.account?.nickname ||
    userData?.providers?.MERCADO_LIBRE?.account?.email ||
    (userData?.providers?.MERCADO_LIBRE?.account?.ml_user_id ? `ML ${userData.providers.MERCADO_LIBRE.account.ml_user_id}` : '') ||
    '';

  if (companyName && integrationName) {
    return `${companyName} · ${integrationName}`;
  }
  return integrationName || companyName || t('INTEGRATIONS.TOPBAR.DEFAULT_NAME');
};

const buildCurrentSummary = (userData, t) => {
  const companyName = userData?.company?.name || '';
  const integrationName =
    userData?.activeIntegration?.display_name ||
    userData?.providers?.MERCADO_LIBRE?.account?.nickname ||
    userData?.providers?.MERCADO_LIBRE?.account?.email ||
    (userData?.providers?.MERCADO_LIBRE?.account?.ml_user_id ? `ML ${userData.providers.MERCADO_LIBRE.account.ml_user_id}` : '') ||
    '';

  return {
    companyName,
    integrationName,
    label: integrationName || companyName || t('INTEGRATIONS.TOPBAR.DEFAULT_NAME')
  };
};

const IntegrationSwitcherDropdown = ({ centered, mobileOnly }) => {
  const { t } = useTranslation();
  const { userData, switchContext } = useAuth();
  const [switchingId, setSwitchingId] = useState(null);
  const companies = Array.isArray(userData?.companies) ? userData.companies : [];
  const mlAccounts = userData?.providers?.MERCADO_LIBRE?.accounts || [];
  const currentLabel = buildCurrentLabel(userData, t);
  const currentSummary = buildCurrentSummary(userData, t);
  const activeIntegrationId = userData?.activeIntegration?.id;
  const activeProvider = userData?.activeIntegration?.provider || 'MERCADO_LIBRE';

  const groupedOptions = useMemo(
    () => {
      const grouped = companies.map(company => ({
        ...company,
        accounts: mlAccounts.filter(account => account.company_id === company.company_id)
      }));
      if (grouped.some(company => company.accounts?.length)) {
        return grouped;
      }
      if (userData?.activeIntegration) {
        return [{
          company_id: userData?.company?.id || 'active',
          company_name: userData?.company?.name || t('INTEGRATIONS.TOPBAR.CURRENT_COMPANY'),
          accounts: [{
            id: userData.activeIntegration.id,
            company_id: userData?.company?.id || null,
            display_name: userData.activeIntegration.display_name || currentLabel,
            is_active: true,
            account: userData?.providers?.MERCADO_LIBRE?.account || {}
          }]
        }];
      }
      return grouped;
    },
    [companies, currentLabel, mlAccounts, userData]
  );

  if (!groupedOptions.some(company => company.accounts?.length)) {
    return null;
  }

  const handleSelect = async (companyId, integrationId) => {
    if (String(integrationId) === String(activeIntegrationId)) {
      return;
    }
    setSwitchingId(integrationId);
    try {
      await switchContext({ companyId, integrationId });
      window.location.reload();
    } catch (error) {
      toast.error(
        error?.response?.data?.error || t('INTEGRATIONS.TOPBAR.SWITCH_ERROR'),
        { theme: 'colored' }
      );
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <Dropdown
      navbar={!centered && !mobileOnly}
      as={centered || mobileOnly ? 'div' : 'li'}
      className={mobileOnly ? 'd-lg-none' : centered ? 'd-none d-lg-flex integration-switcher-nav' : ''}
      data-tour={centered ? 'context-switcher' : undefined}
    >
      <Dropdown.Toggle
        bsPrefix="toggle"
        aria-label={t('INTEGRATIONS.TOPBAR.VISIBLE_ACCOUNT')}
        className={
          mobileOnly
            ? 'integration-switcher-trigger integration-switcher-mobile-toggle nav-link d-flex align-items-center justify-content-center text-body'
            : centered
              ? 'integration-switcher-trigger d-flex align-items-center justify-content-between gap-2 text-start'
              : 'nav-link px-2 d-flex align-items-center gap-2 text-body'
        }
      >
        <div className="d-flex align-items-center gap-2 min-w-0">
          <ProviderAvatar provider={activeProvider} size={centered || mobileOnly ? 'm' : 's'} />
          {!mobileOnly && (
            <div className="min-w-0">
              {centered ? (
                <>
                  <div className="fs--2 lh-1 mb-1 text-600 text-truncate integration-switcher-eyebrow">
                    {currentSummary.companyName || t('INTEGRATIONS.TOPBAR.ACTIVE_LABEL')}
                  </div>
                  <div className="fw-semi-bold text-truncate integration-switcher-current-name">
                    {currentSummary.label}
                  </div>
                </>
              ) : (
                <span className="d-none d-lg-inline-block text-truncate" style={{ maxWidth: 220 }}>
                  {currentLabel}
                </span>
              )}
            </div>
          )}
        </div>
        <FontAwesomeIcon icon="chevron-down" className="fs--2 text-600 flex-shrink-0" />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="dropdown-caret dropdown-menu-card dropdown-menu-end py-2 integration-switcher-menu"
        style={{ minWidth: centered ? 320 : 280 }}
      >
        <div className="px-3 pb-2">
          <div className="fw-semi-bold">{t('INTEGRATIONS.TOPBAR.VISIBLE_ACCOUNT')}</div>
          <div className="fs--2 text-truncate">{currentLabel}</div>
        </div>
        <Dropdown.Divider />
        {groupedOptions.map(company => (
          <div key={company.company_id}>
            <div className="px-3 pt-2 pb-1  fs--2 text-uppercase fw-semi-bold">
              {company.company_name}
            </div>
            {(company.accounts || []).map(account => {
              const isActive = account.is_active || String(account.id) === String(activeIntegrationId);
              const isSwitching = switchingId === account.id;
              return (
                <Dropdown.Item
                  key={account.id}
                  aria-current={isActive ? 'true' : undefined}
                  disabled={isSwitching}
                  onClick={() => handleSelect(company.company_id, account.id)}
                  className={`integration-switcher-option d-flex align-items-start justify-content-between gap-3 ${
                    isActive ? 'is-active' : ''
                  }`}
                >
                  <div className="d-flex align-items-center gap-2 min-w-0 flex-grow-1">
                    <ProviderAvatar provider={account.provider} size="m" />
                    <div className="min-w-0">
                      <div className="fw-semi-bold text-truncate">
                        {account.display_name || account.account?.nickname || account.account?.email || (account.account?.ml_user_id ? `ML ${account.account.ml_user_id}` : `${t('INTEGRATIONS.TOPBAR.INTEGRATION_LABEL')} ${account.id}`)}
                      </div>
                      <div className="fs--2 text-truncate">
                        {account.account?.nickname
                          ? `@${account.account.nickname}`
                          : account.account?.email || account.account?.ml_user_id || t('INTEGRATIONS.MERCADO_LIBRE.TITLE')}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {isSwitching ? (
                      <Spinner animation="border" size="sm" />
                    ) : isActive ? (
                      <FontAwesomeIcon icon="check" className="text-success" />
                    ) : null}
                  </div>
                </Dropdown.Item>
              );
            })}
          </div>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

IntegrationSwitcherDropdown.propTypes = {
  centered: PropTypes.bool,
  mobileOnly: PropTypes.bool
};

IntegrationSwitcherDropdown.defaultProps = {
  centered: false,
  mobileOnly: false
};

export default IntegrationSwitcherDropdown;
